<template>
  <section>
    <div class="account-detail">
      <div>
        <AdminDataTable
          ref="accountListTable"
          @onAction="actions.showAddAccountDialog = true"
          :headers="headers"
          :data="sanitizedItems"
          :accountsData="accountItems"
          :loading="loading"
          :recalculateReadinessInFlightId="recalculateInFlightAccountId"
          @rowItemClick="showRowActions"
          @appliedFilter="appliedFilter($event)"
          :pagination="paginationObj"
          @onAccountClick="redirectToAccount"
          use-server-query
          :remote-list-has-matches="accountListHasHadRows"
          :header-active-count="$store.getters.activeAccountCount"
          @server-accounts-search="onServerAccountsSearch"
          @server-accounts-page="onServerAccountsPage"
          @server-accounts-clear="onServerAccountsClear"
        />
      </div>
    </div>
    <AdminCreateAccountDialog
      v-model="actions.showAddAccountDialog"
      @input="onCloseModal"
      @onSave="saveAccount"
      @clearError="accountNameError = ''"
      :current-account="currentAccount"
      :existingNames="sanitizedItems.map((el) => el.account)"
      :account-name-error="accountNameError"
    />
    <AdminActivateAccount
      v-model="actions.showActivateAccountDialog"
      :current-account="currentAccount"
      @input="onCloseModal"
      @onSave="onActivateAccount"
      :existingUsers="existingUsers"
    />
    <ExReOpenAccount
      v-model="actions.showReOpenAccountDialog"
      :current-account="currentAccount"
      @input="onCloseModal"
      @onAction="reOpenAccount"
    />
    <ExChangeAccountOwner
      v-model="actions.showChangeOwnerDialog"
      @checkEmail="checkEmailForGiveAccess"
      @onChangeContentType="giveAccessContentTypeProp = $event"
      :contentTypeProp="giveAccessContentTypeProp"
      :existingUser="existingUser"
      :current-account="currentAccount"
      @onSave="changeAccountOwner"
    />
    <AdminCloseAccountDialog
      v-model="actions.showCloseAccountDialog"
      :current-account="currentAccount"
      @input="onCloseModal"
      @onSave="onCloseAccount"
    />
    <AdminSuspendAccountDialog
      v-model="actions.showSuspendAccountDialog"
      :current-account="currentAccount"
      @input="onCloseModal"
      @onSave="onSuspendAccount"
    />
    <AdminDeleteAccountDialog
      v-model="actions.showDeleteAccountDialog"
      :current-account="currentAccount"
      @input="onCloseModal"
      @onSave="onDeleteAccount"
    />
    <CancelInviteAdminAccountDialog
      :show="actions.showCancelInviteAccountDialog"
      :account="currentAccount"
      @onClose="onCloseCancelInviteActionDialog"
      @onAction="cancelInviteAdminAccount"
    />
    <RecalculateReadinessDialog
      v-model="actions.showRecalculateReadinessDialog"
      @input="onCloseRecalculateReadinessModal"
      @onConfirm="onRecalculateReadiness"
    />
  </section>
</template>

<script>
import * as Sentry from "@sentry/vue";

import AdminDataTable from "@components/DataTable/AdminDataTable.vue";
import AdminCreateAccountDialog from "@components/AdminCreateAccount/AdminCreateAccountDialog.vue";
import AdminActivateAccount from "@components/AdminActivateAccount/AdminActivateAccountDialog.vue";
import ExReOpenAccount from "@components/ReOpenAccount/ExReOpenAccount.vue";
import ExChangeAccountOwner from "@components/AdminChangeOwner/ExChangeAccountOwner.vue";
import AdminCloseAccountDialog from "@components/AdminCloseAccount/AdminCloseAccountDialog.vue";
import AdminSuspendAccountDialog from "@components/AdminSuspendAccount/AdminSuspendAccountDialog.vue";
import AdminDeleteAccountDialog from "@components/AdminDeleteAccount/AdminDeleteAccountDialog.vue";
import CancelInviteAdminAccountDialog from "@components/AdminAccountActions/CancelInviteAdminAccountDialog.vue";
import RecalculateReadinessDialog from "@components/AdminRecalculateReadiness/RecalculateReadinessDialog.vue";
import {
  LD_FLAG_IDEA_MDP_522,
  LD_FLAG_IDEA_MDP_560,
} from "@components/constants/launchDarklyFlagKeys";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import { getFilters, updateFilters } from "@components/helper/localStorageHelper";

import {
  cancelInviteUser,
  changeOwner,
  closeAccount,
  createAccount,
  createUser,
  deleteAccount,
  getListOfAccounts,
  getSystemUsers,
  openAccount,
  recalculateReadiness,
  reOpenAccount,
  renameAccount,
  resolveAdminAccountOwnerUserIdForCancelInvite,
  suspendAccount,
} from "@/api";
export default {
  name: "AccountList",
  components: {
    AdminCreateAccountDialog,
    AdminDataTable,
    AdminActivateAccount,
    ExReOpenAccount,
    ExChangeAccountOwner,
    AdminCloseAccountDialog,
    AdminSuspendAccountDialog,
    AdminDeleteAccountDialog,
    CancelInviteAdminAccountDialog,
    RecalculateReadinessDialog,
  },
  data() {
    return {
      open: Boolean,
      mdp522Enabled: false,
      paginationObj: {
        page: 1,
        total: 0,
        perPage: 10,
      },
      accountItems: [],
      headers: [
        {
          text: "Account",
          sortable: false,
          value: "account",
          img: "icons/accounts.svg",
        },
        {
          text: "Account Owner",
          sortable: false,
          value: "accountowner",
          img: "icons/owner.svg",
        },
        {
          text: "Subscription",
          sortable: false,
          value: "subscription",
          img: "icons/subscriptions.svg",
        },
        {
          text: "Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      existingUser: null,
      actions: {
        showAddAccountDialog: false,
        showActivateAccountDialog: false,
        showReOpenAccountDialog: false,
        showChangeOwnerDialog: false,
        showCloseAccountDialog: false,
        showSuspendAccountDialog: false,
        showDeleteAccountDialog: false,
        showCancelInviteAccountDialog: false,
        showRecalculateReadinessDialog: false,
      },
      recalculateInFlightAccountId: null,
      currentAccount: null,
      accountNameError: "",
      accountID: null,
      giveAccessContentTypeProp: "step1",
      dataAccounts: [],
      existingUsers: [],
      existingUsersFetchPromise: null,
      dataFilter: [],
      accountsSearchQuery: "",
      accountsSearchDebounceTimer: null,
      accountListHasHadRows: false,
      loading: false,
      skeletonLoadingEnabled: false,
    };
  },
  beforeMount() {
    this.dataFilter = this.readStoredAccountFilters();
  },
  computed: {
    sanitizedItems() {
      return this.dataAccounts.map((el) => {
        const newEl = {};
        newEl.id = el.id;
        newEl.account = el?.name || "";
        newEl.userName = (el?.owner_first_name || "") + " " + (el?.owner_last_name || "");
        newEl.email = el?.owner_email || "";
        newEl.status = el?.status || "draft";
        newEl.status = el?.status === "pending" ? "pending activation" : el?.status;

        return newEl;
      });
    },
  },
  async mounted() {
    try {
      if (this.$getFlag) {
        this.mdp522Enabled = await this.$getFlag(LD_FLAG_IDEA_MDP_522, false);
        await this.checkSkeletonFlag();
      }
      if (!this.skeletonLoadingEnabled) {
        this.$loader.show();
      }
      await this.loadAccounts();
      if (!this.skeletonLoadingEnabled) {
        this.$loader.hide();
      }
    } catch (e) {
      console.log("e: ", e);
    }
  },
  beforeDestroy() {
    if (this.accountsSearchDebounceTimer) {
      clearTimeout(this.accountsSearchDebounceTimer);
    }
  },
  methods: {
    // Account-action toasts (ER-10129) are gated behind the MDP-522 flag.
    notifyMdp522(kind, message) {
      if (this.mdp522Enabled) {
        this.$notifications?.[kind]?.(message);
      }
    },
    async checkSkeletonFlag() {
      try {
        this.skeletonLoadingEnabled = (await this.$getFlag(LD_FLAG_IDEA_MDP_560, false)) === true;
      } catch (error) {
        this.skeletonLoadingEnabled = false;
      }
    },
    readStoredAccountFilters() {
      const filters = getFilters("appliedFilters");
      if (Array.isArray(filters) && filters.length > 0) {
        return filters;
      }
      return getDefaultFiltersForKey("appliedFilters") || [];
    },
    onServerAccountsSearch(query) {
      if (this.accountsSearchDebounceTimer) {
        clearTimeout(this.accountsSearchDebounceTimer);
      }
      const trimmed = (query || "").trim();
      if (trimmed === "") {
        this.accountsSearchQuery = "";
        this.paginationObj.page = 1;
        this.loadAccounts();
        return;
      }
      this.accountsSearchDebounceTimer = setTimeout(() => {
        this.accountsSearchQuery = trimmed;
        this.paginationObj.page = 1;
        this.loadAccounts();
      }, 350);
    },
    onServerAccountsPage(page) {
      this.paginationObj.page = page;
      this.loadAccounts();
    },
    /** Reset both search and filters (Clear All / Clear Search / Clear Filter) in one reload. */
    onServerAccountsClear() {
      if (this.accountsSearchDebounceTimer) {
        clearTimeout(this.accountsSearchDebounceTimer);
        this.accountsSearchDebounceTimer = null;
      }
      this.accountsSearchQuery = "";
      this.dataFilter = [];
      updateFilters("appliedFilters", []);
      this.paginationObj.page = 1;
      this.loadAccounts();
    },
    /**
     * Full user list is only needed for activate / change-owner email checks.
     * Avoids `user/?limit=500` on every Accounts page load.
     */
    async ensureSystemUsersForAccessDialogs() {
      if (this.existingUsers.length > 0) {
        return;
      }
      if (this.existingUsersFetchPromise) {
        return this.existingUsersFetchPromise;
      }
      this.existingUsersFetchPromise = getSystemUsers()
        .then((res) => {
          this.existingUsers = res.data.results || [];
        })
        .catch((e) => {
          console.error("error loading existing users: ", e);
        })
        .finally(() => {
          this.existingUsersFetchPromise = null;
        });
      return this.existingUsersFetchPromise;
    },
    async appliedFilter(data) {
      this.dataFilter = data;
      this.paginationObj.page = 1;
      await this.loadAccounts();
    },
    async reOpenAccount() {
      this.$loader.setAppLoading(true);
      try {
        await reOpenAccount(this.currentAccount.id);
        this.notifyMdp522("success", "Account reopened successfully.");
      } catch (e) {
        console.error(e);
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
        this.notifyMdp522("error", detail || "Failed to reopen account.");
      } finally {
        await this.loadAccounts();
        this.currentAccount = null;
        this.existingUser = null;
        this.actions.showReOpenAccountDialog = false;
      }
      this.$loader.setAppLoading(false);
    },
    async cancelInviteAdminAccount() {
      try {
        this.$loader.setAppLoading(true);
        const res = this.accountItems.find((element) => {
          return element.id == this.currentAccount.id;
        });

        if (!res) {
          throw new Error("Account not found or is invalid.");
        }

        const userId = await resolveAdminAccountOwnerUserIdForCancelInvite(res);
        if (!userId) {
          this.$notifications?.error(
            "Could not resolve the account owner's user id for this invite. Check the account access list or try again."
          );
          return;
        }

        await cancelInviteUser(userId);
        await this.loadAccounts();
        this.actions.showCancelInviteAccountDialog = false;
        this.$notifications?.success("Invite cancelled.");
      } catch (e) {
        console.error("error: ", e);
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
        this.$notifications?.error(detail || "Failed to cancel invite.");
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async checkEmailForGiveAccess(email) {
      try {
        this.$loader.setAppLoading(true);
        const userFromEmail = this.existingUsers.find((el) => el.email == email);
        if (userFromEmail) {
          if (userFromEmail.state == "blocked") {
            this.giveAccessContentTypeProp = "emailBlocked";
          } else if (userFromEmail.state == "closed") {
            this.giveAccessContentTypeProp = "emailClosed";
          } else {
            this.existingUser = {
              firstName: userFromEmail.first_name,
              lastName: userFromEmail.last_name,
            };
            this.giveAccessContentTypeProp = "userExist";
          }
        } else {
          this.giveAccessContentTypeProp = "userDoesNotExist";
        }
      } catch (e) {
        console.error("error: ", e);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async changeAccountOwner(payload) {
      this.$loader.setAppLoading(true);
      try {
        const data = {
          first_name: payload.firstName,
          last_name: payload.lastName,
          email: payload.email,
        };
        if (this.existingUser) {
          await changeOwner(this.currentAccount.id, { email: data.email });
        } else {
          await createUser(data);
          await changeOwner(this.currentAccount.id, { email: data.email });
        }
        await this.loadAccounts();
        this.notifyMdp522("success", "Account owner changed successfully.");
      } catch (e) {
        console.error(e);
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
        this.notifyMdp522("error", detail || "Failed to change account owner.");
      } finally {
        this.currentAccount = null;
        this.existingUser = null;
        this.actions.showChangeOwnerDialog = false;
      }
      this.$loader.setAppLoading(false);
    },
    async onActivateAccount(data) {
      this.$loader.setAppLoading(true);
      try {
        await openAccount(this.currentAccount.id, {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
        });
        await this.loadAccounts();
        this.actions.showActivateAccountDialog = false;
        this.notifyMdp522("success", "Account activated successfully.");
      } catch (e) {
        console.error("e: ", e);
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
        this.notifyMdp522("error", detail || "Failed to activate account.");
      }
      this.$loader.setAppLoading(false);
    },
    onCloseModal(show) {
      if (!show) {
        this.currentAccount = null;
      }
    },
    onCloseCancelInviteActionDialog() {
      this.currentAccount = null;
      this.actions.showCancelInviteAccountDialog = false;
    },
    async loadAccounts(opts = {}) {
      this.loading = true;
      if (!this.skeletonLoadingEnabled) {
        this.$loader.show();
      }
      try {
        const { skipActiveCount = false } = opts;
        const perPage = this.paginationObj.perPage;
        const page = this.paginationObj.page;
        const offset = Math.max(0, (page - 1) * perPage);
        const status = this.dataFilter.length > 0 ? [...this.dataFilter] : undefined;
        const search =
          this.accountsSearchQuery && this.accountsSearchQuery.length > 0
            ? this.accountsSearchQuery
            : undefined;

        const listParams = {
          limit: perPage,
          offset,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
        };

        // allSettled (not all) so a network failure on either request can't
        // leak as `onunhandledrejection` (see Sentry 7390056882).
        const calls = [getListOfAccounts(listParams)];
        if (!skipActiveCount) {
          // Active-account count for the header. Skipped on create/rename
          // reloads where the open count cannot have changed (ER-12318).
          calls.push(getListOfAccounts({ limit: 1, offset: 0, status: "open" }));
        }
        const results = await Promise.allSettled(calls);
        const listResult = results[0];

        if (listResult.status === "rejected") {
          const reason = listResult.reason;
          if (reason instanceof Error) {
            throw reason;
          }
          const message = typeof reason === "string" ? reason : "Failed to load accounts";
          throw new Error(message);
        }

        const { data } = listResult.value;
        this.paginationObj.total = Number(data.count) || 0;
        if (this.paginationObj.total > 0) {
          this.accountListHasHadRows = true;
        }
        this.accountItems = (data.results || []).map((item) => {
          if (item.status === "pending") {
            return {
              ...item,
              status: "pending activation",
            };
          }
          return item;
        });
        this.dataAccounts = this.accountItems;
        if (!skipActiveCount) {
          const openResult = results[1];
          if (openResult.status === "fulfilled") {
            const openTotal = Number(openResult.value.data.count) || 0;
            this.$store.commit("setActiveAccountCount", openTotal);
          } else {
            console.warn("Active account count fetch failed:", openResult.reason);
          }
        }
      } catch (error) {
        console.error("Failed to load accounts:", error);
        this.accountItems = [];
        this.dataAccounts = [];
        this.paginationObj.total = 0;
      } finally {
        this.loading = false;
        if (!this.skeletonLoadingEnabled) {
          this.$loader.hide();
        }
      }
    },
    async saveAccount(accountName) {
      this.$loader.setAppLoading(true);
      this.accountNameError = "";
      const trimmedName = (accountName || "").trim();
      try {
        const payload = {
          name: accountName,
        };
        if (this.currentAccount && this.currentAccount.id) {
          await renameAccount(this.currentAccount.id, payload);
          this.accountsSearchQuery = trimmedName;
          this.paginationObj.page = 1;
          await this.$nextTick();
          this.$refs.accountListTable?.syncListQueryFromParent?.({
            searchText: trimmedName,
            appliedFilters: this.dataFilter,
          });
          await this.loadAccounts({ skipActiveCount: true });
          this.currentAccount = null;
          this.actions.showAddAccountDialog = false;
        } else {
          await createAccount(payload);
          // New accounts are drafts; include "draft" so the row is not filtered out.
          if (!this.dataFilter.includes("draft")) {
            this.dataFilter = [...this.dataFilter, "draft"];
            updateFilters("appliedFilters", this.dataFilter);
          }
          this.accountsSearchQuery = trimmedName;
          this.paginationObj.page = 1;
          await this.$nextTick();
          this.$refs.accountListTable?.syncListQueryFromParent?.({
            searchText: trimmedName,
            appliedFilters: this.dataFilter,
          });
          await this.loadAccounts({ skipActiveCount: true });
          this.actions.showAddAccountDialog = false;
        }
      } catch (e) {
        this.handleAccountSaveError(e);
      }
      this.$loader.setAppLoading(false);
    },
    // Normalize a 400 validation payload (array of strings or {name: [...]})
    // into an inline Account Name error, keeping the modal open. Non-400
    // failures surface a generic toast instead. (ER-12318)
    handleAccountSaveError(e) {
      const fieldMessage = this.extractFieldValidationMessage(e);
      if (fieldMessage) {
        this.accountNameError = fieldMessage;
        return;
      }
      const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
      this.$notifications?.error(detail || "Failed to save account. Please try again.");
    },
    extractFieldValidationMessage(e) {
      const data = e?.response?.data;
      if (e?.response?.status !== 400 || data == null) {
        return null;
      }
      if (Array.isArray(data)) {
        return data.length ? String(data[0]) : null;
      }
      if (typeof data !== "object") {
        return String(data);
      }
      const nameErr = data.name;
      if (Array.isArray(nameErr) && nameErr.length) return String(nameErr[0]);
      if (typeof nameErr === "string") return nameErr;
      return null;
    },
    async showRowActions(data) {
      this.currentAccount = data.row;
      switch (data.action.value) {
        case "admin_rename_account":
          this.actions.showAddAccountDialog = true;
          break;
        case "admin_open_account":
          await this.ensureSystemUsersForAccessDialogs();
          this.actions.showActivateAccountDialog = true;
          break;
        case "admin_change_account_owner":
          await this.ensureSystemUsersForAccessDialogs();
          this.actions.showChangeOwnerDialog = true;
          break;
        case "admin_reopen_account":
          this.actions.showReOpenAccountDialog = true;
          break;
        case "admin_close_account":
          this.actions.showCloseAccountDialog = true;
          break;
        case "admin_suspend_account":
          this.actions.showSuspendAccountDialog = true;
          break;
        case "admin_delete_account":
          this.actions.showDeleteAccountDialog = true;
          break;
        case "cancel_invite_action":
          this.currentAccount = this.accountItems.find((element) => {
            return element.id == this.currentAccount.id;
          });
          this.actions.showCancelInviteAccountDialog = true;
          break;
        case "admin_recalculate_readiness":
          this.actions.showRecalculateReadinessDialog = true;
          break;
        default:
          break;
      }
    },
    onPageChange(event) {
      this.paginationObj.page = event;
    },
    async onCloseAccount() {
      this.$loader.setAppLoading(true);
      try {
        await closeAccount(this.currentAccount.id);
        await this.loadAccounts();
        this.actions.showCloseAccountDialog = false;
        this.notifyMdp522("success", "Account closed successfully.");
      } catch (e) {
        console.error("e: ", e);
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
        this.notifyMdp522("error", detail || "Failed to close account.");
      }
      this.$loader.setAppLoading(false);
    },
    async onSuspendAccount() {
      this.$loader.setAppLoading(true);
      try {
        const data = {
          reason: "Suspending account.",
        };
        await suspendAccount(this.currentAccount.id, data);
        await this.loadAccounts();
        this.actions.showSuspendAccountDialog = false;
        this.notifyMdp522("success", "Account suspended successfully.");
      } catch (e) {
        console.error("e: ", e);
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
        this.notifyMdp522("error", detail || "Failed to suspend account.");
      }
      this.$loader.setAppLoading(false);
    },
    async onDeleteAccount() {
      this.$loader.setAppLoading(true);
      try {
        await deleteAccount(this.currentAccount.id);
        await this.loadAccounts();
        this.actions.showDeleteAccountDialog = false;
        this.notifyMdp522("success", "Account deleted successfully.");
      } catch (e) {
        console.error("e: ", e);
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
        this.notifyMdp522("error", detail || "Failed to delete account.");
      }
      this.$loader.setAppLoading(false);
    },
    onCloseRecalculateReadinessModal() {
      this.actions.showRecalculateReadinessDialog = false;
      this.recalculateInFlightAccountId = null;
    },
    notifyRecalculateReadinessError(err) {
      const status = err?.response?.status;
      if (status === 409) {
        this.$notifications?.error("Recalculation already in progress for this account.");
        return;
      }
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      this.$notifications?.error(detail || "Failed to start readiness recalculation.");
      Sentry.captureException(err);
    },
    async onRecalculateReadiness() {
      const accountId = this.currentAccount?.id;
      if (!accountId) return;
      this.recalculateInFlightAccountId = accountId;
      try {
        const response = await recalculateReadiness(accountId);
        if (response?.status === 202) {
          this.$notifications?.success("Readiness recalculation started.");
          this.actions.showRecalculateReadinessDialog = false;
          this.recalculateInFlightAccountId = null;
          await this.loadAccounts();
        }
      } catch (err) {
        this.recalculateInFlightAccountId = null;
        this.notifyRecalculateReadinessError(err);
        this.actions.showRecalculateReadinessDialog = false;
      }
    },
    async redirectToAccount(data) {
      localStorage.setItem("selectedAccount", JSON.stringify(data)); // Fix spacing + stringify
      this.$router.push({
        name: "AccountView",
        params: {
          id: data.id,
        },
      });
    },
  },
};
</script>
<style scoped>
.count-icon-box {
  width: 25px;
  height: 20px;
  border-radius: 100px;
  border: 1px solid #eaecf0;
  text-align: center;
  font-weight: 500;
  color: #344054;
  font-size: 12px;
  background-color: #f9fafb;
}
.account-detail {
  border: 1px solid #eaecf0;
  padding: 1px;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1);
  height: 100%;
}
.account-detail .active-btn {
  background-color: #f9f5ff !important;
  color: #6941c6 !important;
  box-shadow: unset !important;
  border-radius: 16px !important;
  text-transform: lowercase;
  height: 30px !important;
  padding: 10px !important;
  font-weight: 500;
  font-size: 12px !important;
}
.account-title {
  display: flex;
  align-items: center;
  gap: 15px;
}
.account-title h2 {
  font-weight: 500;
  font-size: 18px;
  color: #101828;
}
.add-account-btn {
  background-color: #7f56d9 !important;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05) !important;
  color: #fff !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  text-transform: capitalize !important;
}
.account-table thead tr th {
  background-color: #f9fafb;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
}
.account-table thead tr th:nth-child(1) {
  width: 90px;
}
.account-table thead tr th:nth-child(2) {
  width: 90px;
}
.account-table thead tr th:nth-child(3) {
  width: 90px;
}
.account-table thead tr th:nth-child(4) {
  width: 90px;
}
.account-table tbody tr:hover {
  background-color: transparent !important;
}
.search-filled {
  padding-right: 0px;
}
.search-filled .v-input__slot fieldset {
  border-color: #d0d5dd;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
.search-filled input {
  color: #667085 !important;
}
.active-chip-box .v-chip--label {
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  margin-left: 0px !important;
}

.filter_wrp img {
  margin-right: 6px;
}

.chip_wrp {
  background-color: transparent !important;
  padding: 10px 14px;
  border-radius: 10px !important;
  margin-right: 10px;
  height: 36px !important;
  font-weight: 600;
  color: #344054;
  border: 1px solid #d0d5dd;
}

.filter_wrp {
  background-color: transparent !important;
  padding: 10px 14px !important;
  border-radius: 10px !important;
  margin-right: 10px;
  height: 36px !important;
  font-weight: 600;
  color: #344054;
  box-shadow: none !important;
  border: 1px solid #d0d5dd;
}

.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 24px !important;
}

.text_error .v-text-field__details {
  position: absolute !important;
  left: 0 !important;
  bottom: -21px !important;
}

::v-deep(.account-name) {
  padding: 15px 20px !important;
}
::v-deep(.account-name):hover {
  padding: 15px 20px !important;
  background-color: #f2edff;
  box-shadow: 0 4px 4px 0 hsla(0, 0%, 40%, 0.1);
  border-radius: 8px;
  cursor: url("../assets/cursorArrow.svg"), auto !important;
}
</style>
